import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  UserCheck, 
  Clock, 
  Upload, 
  LogOut,
  Shield,
  Ticket,
  TrendingUp,
  Activity} from 'lucide-react';
import { APP_CONFIG } from '../../utils/constants';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

interface ApiResponse {
  stats: {
    users: {
      total: number;
      notVerified: number;
      semiVerified: number;
      verified: number;
      emailVerified: number;
      phoneVerified: number;
      newThisWeek: number;
      newThisMonth: number;
    };
    profiles: {
      total: number;
      online: number;
      offline: number;
      male: number;
      female: number;
      neverMarried: number;
      reddyCaste: number;
      averageAge: number;
      withPhotos: number;
    };
    verifications: {
      pending: number;
      recentlyVerified: number;
      averageVerificationTime: string;
    };
    activity: Array<{
      action: string;
      count: number;
    }>;
    recentActivities: Array<{
      id: number;
      action: string;
      entityType: string;
      entityId: string | null;
      userEmail: string | null;
      adminUsername: string | null;
      details: any;
      ipAddress: string;
      createdAt: string;
    }>;
  };
  lastUpdated: string;
}

const AdminDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddEligible, setShowAddEligible] = useState(false);
  const [showCreateCoupon, setShowCreateCoupon] = useState(false);
  const [eligibleUsersText, setEligibleUsersText] = useState('');
  const [couponData, setCouponData] = useState({
    code: '',
    discount: '',
    expiryDate: ''
  });

  const navigate = useNavigate();
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    setLoading(true);
    try {
      const response = await apiService.getDashboardStats();
      setDashboardData(response);
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  const handleAddEligibleUsers = async () => {
    if (!eligibleUsersText.trim()) return;

    try {
      const lines = eligibleUsersText.trim().split('\n');
      const users = lines.map(line => {
        const [email, phone] = line.trim().split(',');
        return { 
          email: email?.trim() || '', 
          phone: phone?.trim() || '' 
        };
      }).filter(user => user.email && user.phone);

      if (users.length === 0) {
        alert('Please enter valid email,phone pairs');
        return;
      }

      await apiService.addEligibleUsers(users);
      alert(`Added ${users.length} eligible users successfully`);
      setEligibleUsersText('');
      setShowAddEligible(false);
      loadDashboardStats();
    } catch (error: any) {
      alert(error.message || 'Failed to add eligible users');
    }
  };

  const handleCreateCoupon = async () => {
    if (!couponData.code || !couponData.discount) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await apiService.createCoupon({
        code: couponData.code,
        discount: parseInt(couponData.discount),
        expiryDate: couponData.expiryDate,
        isActive: true
      });
      
      alert('Coupon created successfully');
      setCouponData({ code: '', discount: '', expiryDate: '' });
      setShowCreateCoupon(false);
      loadDashboardStats();
    } catch (error: any) {
      alert(error.message || 'Failed to create coupon');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAction = (action: string) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return <LoadingSpinner fullScreen text="Loading dashboard..." />;
  }

  const stats = dashboardData?.stats;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Admin Dashboard</h1>
                <p className="text-sm text-gray-500">{APP_CONFIG.name}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Welcome, {adminUser.username}</span>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-1 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Users</p>
                <p className="text-2xl font-semibold text-gray-900">{stats?.users.total || 0}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats?.users.newThisWeek || 0} new this week
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Verifications</p>
                <p className="text-2xl font-semibold text-orange-600">{stats?.verifications.pending || 0}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Avg: {stats?.verifications.averageVerificationTime || 'N/A'}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-lg">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Verified Users</p>
                <p className="text-2xl font-semibold text-green-600">{stats?.users.verified || 0}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats?.verifications.recentlyVerified || 0} recently verified
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <UserCheck className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Profiles</p>
                <p className="text-2xl font-semibold text-purple-600">{stats?.profiles.online || 0}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats?.profiles.total || 0} total profiles
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Profile Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Demographics</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Male Profiles</span>
                <div className="flex items-center space-x-2">
                  <div className="w-20 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${stats?.profiles.total ? (stats.profiles.male / stats.profiles.total) * 100 : 0}%` }}
                    ></div>
                  </div>
                  <span className="font-medium text-sm">{stats?.profiles.male || 0}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Female Profiles</span>
                <div className="flex items-center space-x-2">
                  <div className="w-20 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-pink-600 h-2 rounded-full" 
                      style={{ width: `${stats?.profiles.total ? (stats.profiles.female / stats.profiles.total) * 100 : 0}%` }}
                    ></div>
                  </div>
                  <span className="font-medium text-sm">{stats?.profiles.female || 0}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Never Married</span>
                <span className="font-medium">{stats?.profiles.neverMarried || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Average Age</span>
                <span className="font-medium">{stats?.profiles.averageAge || 0} years</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Profiles with Photos</span>
                <span className="font-medium">{stats?.profiles.withPhotos || 0}</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/admin/verify')}
                className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <UserCheck className="w-5 h-5 text-orange-600" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900">Verify Users</p>
                    <p className="text-sm text-gray-500">Review pending verifications</p>
                  </div>
                </div>
                {stats?.verifications.pending && stats.verifications.pending > 0 && (
                  <span className="bg-orange-100 text-orange-600 text-xs font-medium px-2 py-1 rounded-full">
                    {stats.verifications.pending}
                  </span>
                )}
              </button>

              <button
                onClick={() => setShowAddEligible(true)}
                className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Upload className="w-5 h-5 text-blue-600" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900">Add Eligible Users</p>
                    <p className="text-sm text-gray-500">Add pre-approved user emails & phones</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setShowCreateCoupon(true)}
                className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Ticket className="w-5 h-5 text-green-600" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900">Create Coupon</p>
                    <p className="text-sm text-gray-500">Generate discount coupons</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Activity Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Summary</h3>
            <div className="space-y-3">
              {stats?.activity.map((activity, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Activity className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium">{formatAction(activity.action)}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{activity.count}</span>
                </div>
              )) || (
                <p className="text-sm text-gray-500">No activity data available</p>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h3>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {stats?.recentActivities.map((activity, index) => (
                <div key={activity.id} className="flex items-start space-x-3 p-3 border border-gray-100 rounded-lg">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {formatAction(activity.action)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {activity.userEmail && `User: ${activity.userEmail}`}
                      {activity.adminUsername && ` • Admin: ${activity.adminUsername}`}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatDate(activity.createdAt)}
                    </p>
                  </div>
                </div>
              )) || (
                <p className="text-sm text-gray-500">No recent activities</p>
              )}
            </div>
          </div>
        </div>

        {/* System Info */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">System Information</h3>
            <div className="flex items-center space-x-2 text-green-600">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium">System Online</span>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{stats?.users.emailVerified || 0}</p>
              <p className="text-sm text-gray-500">Email Verified</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{stats?.users.phoneVerified || 0}</p>
              <p className="text-sm text-gray-500">Phone Verified</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{stats?.users.newThisMonth || 0}</p>
              <p className="text-sm text-gray-500">New This Month</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{stats?.profiles.reddyCaste || 0}</p>
              <p className="text-sm text-gray-500">Reddy Community</p>
            </div>
          </div>
          {dashboardData?.lastUpdated && (
            <p className="text-xs text-gray-400 mt-4 text-center">
              Last updated: {formatDate(dashboardData.lastUpdated)}
            </p>
          )}
        </div>
      </div>

      {/* Add Eligible Users Modal */}
      {showAddEligible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Eligible Users</h3>
            <p className="text-sm text-gray-600 mb-4">
              Enter email,phone pairs (one per line):
            </p>
            <textarea
              value={eligibleUsersText}
              onChange={(e) => setEligibleUsersText(e.target.value)}
              rows={6}
              className="w-full border border-gray-300 rounded-lg p-3 text-sm"
              placeholder="user1@email.com,9876543210&#10;user2@email.com,9876543211"
            />
            <div className="flex space-x-3 mt-4">
              <button
                onClick={() => setShowAddEligible(false)}
                className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddEligibleUsers}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Add Users
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Coupon Modal */}
      {showCreateCoupon && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Coupon</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code</label>
                <input
                  type="text"
                  value={couponData.code}
                  onChange={(e) => setCouponData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  className="w-full border border-gray-300 rounded-lg p-2"
                  placeholder="WELCOME50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount (%)</label>
                <input
                  type="number"
                  value={couponData.discount}
                  onChange={(e) => setCouponData(prev => ({ ...prev, discount: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg p-2"
                  placeholder="50"
                  min="0"
                  max="100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date (Optional)</label>
                <input
                  type="date"
                  value={couponData.expiryDate}
                  onChange={(e) => setCouponData(prev => ({ ...prev, expiryDate: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg p-2"
                />
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowCreateCoupon(false)}
                className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCoupon}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;